import { ref, computed, defineAsyncComponent, markRaw, watchEffect } from 'vue'
import { defineStore } from 'pinia';
import { useSpotStore } from '@/stores/SpotStore';
import { useUserStore } from '@/stores/UserStore';
import { useAlertStore } from '@/stores/AlertStore';
import { useOnsiteReportsStore } from '@/stores/OnsiteReportsStore';
import { useProForecastStore } from '@/stores/ProForecastStore';
import { useBulletinStore } from '@/stores/BulletinStore';
import { useUserUpgrades } from '@/composables/useUserUpgrades';
import useStorageSystem from '@/composables/useStorageSystem';
import { useMediaQuery } from '@/composables/useMediaQuery';
import useSpotGlobals from '@/composables/useSpotGlobals';
import useSettingsGlobals from '@/composables/useSettingsGlobals'
import useConfigurationGlobals from '@/composables/useConfigurationGlobals'
import useContainerConfigurationGlobals from '@/composables/useContainerConfigurationGlobals'

const { $IS_ARBITRARY_SPOT } = useSpotGlobals();
const globalSettings = useSettingsGlobals();
const { $WEB_VIEW } = globalSettings;  
const { $BRAND } = useConfigurationGlobals()
const { $WIND_GRAPH } = useContainerConfigurationGlobals()

export let useCompleteReportStore = defineStore('completeReportStore', () => {
    const userStore = useUserStore();
    const spotStore = useSpotStore();
    const alertStore = useAlertStore();
    const onsiteReportsStore = useOnsiteReportsStore();
    const proForecastStore = useProForecastStore();
    const bulletinStore = useBulletinStore();

    const { upgrade, upgradeSpot } = useUserUpgrades()

    const isDesktop = useMediaQuery('(min-width: 840px)');

    const defaultContainers = [101, 100, 1, 2, 11, 3, 4, 10, 6, 5, 7, 8, 9];
    
    const showCompleteReportSyncControl = Object.prototype.hasOwnProperty.call(globalSettings, 'show_complete_report_sync_control') ? globalSettings.show_complete_report_sync_control : false;

    const windGraphStartingViewSettingId= 'crwindGraphStartingView';
    const proForecastStartingViewSettingId = 'crProForecastStartingView';
    const currentConditionsStartingViewSettingId = 'crCurrentConditionsStartingView';
    const tidesShowDataTablesSettingId = 'crTidesShowTideEventTables';
    const tidesShowGraphSettingId = 'crTidesShowGraph';
    const tidesShowPreviewGraphSettingId = 'crTidesShowPreviewGraph';

    const isLoading = ref(true);
    const error = ref(null);
    const containers = ref([]);
    const isArbitrarySpot = ref($IS_ARBITRARY_SPOT);
    const showSettingsModal = ref(false);

    const [activeContainers] = useStorageSystem(defaultContainers, 'crActiveContainers', 'apiUserStorage');
    const [containerOrder, crContainerOrderLoaded] = useStorageSystem(defaultContainers, 'crContainerOrder', 'apiUserStorage');
    const [lastUpdateTime] = useStorageSystem('timestamp', 'crLastUpdateTime', 'apiUserStorage');
    const [windGraphStartingView] = useStorageSystem($WIND_GRAPH.default_graph, windGraphStartingViewSettingId, 'apiUserStorage');
    const [proForecastStartingView] = useStorageSystem('collapsed', proForecastStartingViewSettingId, 'apiUserStorage');
    const [currentConditionsStartingView] = useStorageSystem('condensed', currentConditionsStartingViewSettingId, 'apiUserStorage');
    const [tidesShowDataTables] = useStorageSystem(true, tidesShowDataTablesSettingId, 'apiUserStorage');
    const [tidesShowGraph] = useStorageSystem(true, tidesShowGraphSettingId, 'apiUserStorage');
    const [tidesShowPreviewGraph] = useStorageSystem(true, tidesShowPreviewGraphSettingId, 'apiUserStorage');

    const storedData = computed(() => ({
      crActiveContainers: activeContainers.value,
      crContainerOrder: containerOrder.value,
      crLastUpdateTime: lastUpdateTime.value,
      [windGraphStartingViewSettingId]: windGraphStartingView.value,
      [proForecastStartingViewSettingId]: proForecastStartingView.value,
      [currentConditionsStartingViewSettingId]: currentConditionsStartingView.value,
      [tidesShowDataTablesSettingId]: tidesShowDataTables.value,
      [tidesShowGraphSettingId]: tidesShowGraph.value,
      [tidesShowPreviewGraphSettingId]: tidesShowPreviewGraph.value,
    }));

    const storedDataWebView = computed(() => {
      let data = [];

      for (const key in storedData.value) {
        data.push(`${key}:${storedData.value[key]}`);
      }

      return data.join(';');
    });

    const webViewMessenger = ref(null);
    const webViewMessengerUnwatch = ref(null);

    const requests = computed(() => {
      if (error.value) return;

      let promises = [];

      if (spotStore.hasSpotProduct(10)) promises.push(alertStore.getAdvisoryByLatLon(spotStore.lat, spotStore.lon));
      if (spotStore.hasSpotProduct(4)) promises.push(onsiteReportsStore.getOnsiteReports(spotStore.lat, spotStore.lon));
      if (spotStore.hasSpotProduct(6)) promises.push(bulletinStore.getBulletins(spotStore.lat, spotStore.lon));

      // Need to get proforecast on page load for the wind graph pro forecast model
      if (showProForecast.value && userStore.memberLevel >= 49) promises.push(proForecastStore.getProForecast(spotStore.proForecastIdIfExists))

      return promises;
    });

    const showProForecast = computed(() => spotStore.hasSpotProduct(3) && activeContainers.value.includes(3) && proForecastStore.canShowProForecast);
    const showOnsiteReports = computed(() => spotStore.hasSpotProduct(4) && activeContainers.value.includes(4) && onsiteReportsStore.hasOnsiteReports);
    const showAdvisories = computed(() => spotStore.hasSpotProduct(10) && activeContainers.value.includes(101) && alertStore.hasAlerts);
    const showMarineForecasts = computed(() => spotStore.hasSpotProduct(6) && activeContainers.value.includes(6) && bulletinStore.hasBulletins );
    const showNearbyTides = computed(() => spotStore.hasSpotProduct(5) && activeContainers.value.includes(5));
    const showWindGraph = computed(() => spotStore.hasSpotProduct(1) && activeContainers.value.includes(1) && !isArbitrarySpot.value && !spotStore.isNowcast);
    const showForecastTable = computed(() => spotStore.hasSpotProduct(2) && activeContainers.value.includes(2));
    const showArchiveData = computed(() => spotStore.hasSpotProduct(7) && activeContainers.value.includes(7));
    const showStatsData = computed(() => spotStore.hasSpotProduct(8) && activeContainers.value.includes(8));
    const showInfoData = computed(() => spotStore.hasSpotProduct(9) && activeContainers.value.includes(9));
    const showCurrentConditions = computed(() => activeContainers.value.includes(100));
    const showGorgeWindbow = computed(() => spotStore.hasSpotProduct(11));   
    const showWindGraphStartingView = computed(() => isDesktop.value && !$WEB_VIEW);    
    const showCurrentConditionsStartingView = computed(() => !isDesktop.value)    
    const showPremiumModelUpgradeBlock = computed(() => spotStore.modelUpgradeAvailable && !spotStore.proForecastIdIfExists);    
    const showPremiumProModelUpgradeBlock = computed(() => spotStore.modelUpgradeAvailable && spotStore.proForecastIdIfExists);    
    const showProModelUpgradeBlock = computed(() => userStore.memberLevel >= 46 && userStore.memberLevel < 49 && spotStore.proForecastIdIfExists && $BRAND.toLowerCase() !== 'windalert');    
    const showModelUpgradeBlock = computed(() => showPremiumModelUpgradeBlock.value || showProModelUpgradeBlock.value);
    const upgradeBlockText = computed(() => showProModelUpgradeBlock.value ? 'all <strong>Pro Models</strong>' : 'all <strong>Premium Models</strong>');

    const spotId = computed(() => spotStore.spotId);
    const getContainers = computed(() => containers.value);
    
    const userChangedSettings = computed(() => {
      let hasChanged = false;
      
      if (windGraphStartingView.value !== 'basic' || proForecastStartingView.value !== 'collapsed' || currentConditionsStartingView.value !== 'condensed') {
        hasChanged = true;
      };

      for (let i = 0; i < defaultContainers.length; i++) {
        const container = defaultContainers[i];

        if (!activeContainers.value.includes(container)) hasChanged = true;
        if (containerOrder.value.indexOf(container) !== i) hasChanged = true
      }
        
      return hasChanged;
    });

    const windGraphContainerSettings = [
      {
        id: windGraphStartingViewSettingId,
        label: 'Starting Graph',
        type: 'segmentedControl',
        model: windGraphStartingView,
        defaultValue: windGraphStartingView.value,
        options: [
          { value: 'basic', label: 'Basic' },
          { value: 'detailed', label: 'Detailed' }
        ],
        show: () => showWindGraphStartingView.value
      }
    ]

    const tidesContainerSettings = [
      {
        id: tidesShowDataTablesSettingId,
        label: 'Show Data Tables',
        type: 'toggle',
        model: tidesShowDataTables,
        defaultValue: tidesShowDataTables.value,
        show: () => true
      },
      {
        id: tidesShowGraphSettingId,
        label: 'Show Graph',
        type: 'toggle',
        model: tidesShowGraph,
        defaultValue: tidesShowGraph.value,
        show: () => true
      },
      {
        id: tidesShowPreviewGraphSettingId,
        label: 'Show Preview Graph',
        type: 'toggle',
        model: tidesShowPreviewGraph,
        defaultValue: tidesShowPreviewGraph.value,
        show: () => true
      }
    ];

    const proForecastContainerSettings = [
      {
        id: proForecastStartingViewSettingId,
        label: 'Starting View',
        type: 'segmentedControl',
        model: proForecastStartingView,
        defaultValue: proForecastStartingView.value,
        options: [
          { value: 'collapsed', label: 'Collapsed' },
          { value: 'expanded', label: 'Expanded' }
        ],
        show: () => true
      }
    ]

    const currentConditionsContainerSettings = [
      {
        id: currentConditionsStartingViewSettingId,
        label: 'Starting View',
        type: 'segmentedControl',
        model: currentConditionsStartingView,
        defaultValue: currentConditionsStartingView.value,
        options: [
          { value: 'condensed', label: 'Condensed' },
          { value: 'expanded', label: 'Expanded' }
        ],
        show: () => showCurrentConditionsStartingView.value
      }
    ]

    // TODO: change the variable names of the settings to be more descriptive
    const components = {
      1: {
        component: markRaw(defineAsyncComponent(
          () => import('@/components/WindGraphsDataContainerSection.vue')
        )),
        settings: windGraphContainerSettings,
        allowReorder: true,
        allowHidden: true,
        props: {
          title: 'Wind Graph',
          upgrade: upgradeSpot,
          settings: getSettingsProps(windGraphContainerSettings)
        },
        show: () => showWindGraph.value
      },
      2: {
        component: markRaw(defineAsyncComponent(
          () => import('@/components/ForecastTableDataContainerSection.vue')
        )),
        allowReorder: true,
        allowHidden: true,
        props: { title: 'Forecast Table', upgrade: upgrade },
        show: () => showForecastTable.value
      },
      3: {
        component: markRaw(defineAsyncComponent(
          () => import('@/components/ProForecastDataContainerSection.vue')
        )),
        settings: proForecastContainerSettings,
        allowReorder: true,
        allowHidden: true,
        props: {
          title: 'Pro Forecast',
          memberLevel: userStore.memberLevel,
          settings: getSettingsProps(proForecastContainerSettings)
        },
        show: () => showProForecast.value
      },
      4: {
        component: markRaw(defineAsyncComponent(
          () => import('@/components/OnsiteReportsDataContainerSection.vue')
        )),
        allowReorder: true,
        allowHidden: true,
        props: { title: 'Onsite Reports', upgrade: upgrade },
        show: () => showOnsiteReports.value
      },
      5: {
        component: markRaw(defineAsyncComponent(
          () => import('@/components/NearbyTidesDataContainerSection.vue')
        )),
        settings: tidesContainerSettings,
        allowReorder: true,
        allowHidden: true,
        props: {
          title: 'Nearby Tides',
          upgrade: upgrade,
          settings: getSettingsProps(tidesContainerSettings)
        },
        show: () => showNearbyTides.value
      },
      6: {
        component: markRaw(defineAsyncComponent(
          () => import('@/components/MarineForecastDataContainerSection.vue')
        )),
        allowReorder: true,
        allowHidden: true,
        props: { title: 'Marine Forecast', upgrade: upgrade },
        show: () => showMarineForecasts.value
      },
      7: {
        component: markRaw(defineAsyncComponent(
          () => import('@/components/ArchiveDataContainerSection.vue')
        )),
        allowReorder: true,
        allowHidden: true,
        props: { title: 'Archive Data', upgrade: upgrade },
        show: () => showArchiveData.value
      },
      8: {
        component: markRaw(defineAsyncComponent(() => import('@/components/StatsDataContainerSection.vue'))),
        allowReorder: true,
        allowHidden: true,
        props: { title: 'Stats', upgrade: upgrade, spotId: spotId },
        show: () => showStatsData.value
      },
      9: {
        component: markRaw(defineAsyncComponent(
          () => import('@/components/StationInfoDataContainerSection.vue')
        )),
        allowReorder: true,
        allowHidden: true,
        props: { title: 'Station Info', upgrade: upgrade },
        show: () => showInfoData.value
      },
      10: {
        component: markRaw(defineAsyncComponent(
          () => import('@/components/AdvisoriesDataContainerSection.vue')
        )),
        allowReorder: true,
        allowHidden: true,
        props: { title: 'Advisories', upgrade: upgrade },
        show: () => showAdvisories.value
      },
      11: {
        component: markRaw(defineAsyncComponent(
          () => import('@/components/GorgeWindbowDataContainerSection.vue')
        )),
        allowReorder: true,
        allowHidden: true,
        props: { title: 'Gorge Windbow', upgrade: upgrade },
        show: () => showGorgeWindbow.value
      },
      100: {
        component: markRaw(defineAsyncComponent(
          () => import('@/components/CurrentConditionsContainerSection.vue')
        )),
        settings: currentConditionsContainerSettings,
        allowReorder: true,
        allowHidden: true,
        props: {
          title: 'Current Conditions',
          containerTypeId: 'Current Conditions',
          upgrade: upgrade,
          settings: getSettingsProps(currentConditionsContainerSettings)
        },
        show: () => showCurrentConditions.value
      },
      101: {
        component: markRaw(defineAsyncComponent(() => import('@/components/AlertWidget.vue'))),
        allowReorder: true,
        allowHidden: true,
        props: { title: 'Advisory Overview' },
        show: () => showAdvisories.value
      },
      1000: {
        component: markRaw(defineAsyncComponent(() => import('@/components/UpgradeBlock.vue'))),
        allowReorder: true,
        allowHidden: true,
        props: { productText: upgradeBlockText, upgrade: upgrade },
        show: () => showModelUpgradeBlock.value
      },
      1001: {
        component: markRaw(defineAsyncComponent(
          () => import('@/components/UpgradeBlockPremiumProModels.vue')
        )),
        allowReorder: true,
        allowHidden: true,
        props: { upgrade: upgrade },
        show: () => showPremiumProModelUpgradeBlock.value
      },
      1100: {
        component: markRaw(defineAsyncComponent(() => import('@/components/BannerAd.vue'))),
        allowReorder: true,
        allowHidden: true,
        props: { type: 'mid' },
        show: () => true
      }
    }    

    const addContainer = (container) => containers.value.push(container);
    const insertContainer = (container, index) => containers.value.splice(index, 0, container);
    const clearContainers = () => containers.value = [];
    const resetContainers = () => {
      activeContainers.value = [...defaultContainers];
      containerOrder.value = [...defaultContainers];
      windGraphStartingView.value = 'basic';
      proForecastStartingView.value = 'collapsed';
      currentConditionsStartingView.value = 'condensed';
    };

    const updateLoadingStatus = (newStatus) => isLoading.value = newStatus;

    function getSettingsProps(settings) {
      let settingsProps = {};

      settings.forEach(setting => settingsProps[setting.id] = setting.model);

      return settingsProps
    }

    function containerCheck() {
      const newContainers = [];
      const mergedContainers = [...containerOrder.value];
      let updateStorage = false;

      defaultContainers.forEach((container) => {
        if (!mergedContainers.includes(container)) {
          newContainers.push(container);
          updateStorage = true;
        };
      });

      mergedContainers.forEach((container, i) => {
        if (!defaultContainers.includes(container)) {
          mergedContainers.splice(i, 1);
          updateStorage = true;
        };
      })

      if (newContainers.length > 0) {
        newContainers.forEach((container) => {
          const defaultIndex = defaultContainers.indexOf(container);
          const defaultPrevContainer = defaultContainers[defaultIndex - 1];
          const defaultNextContainer = defaultContainers[defaultIndex + 1];
          let insertIndex = mergedContainers.length - 1;

          if (mergedContainers.includes(defaultPrevContainer)) {
            insertIndex = mergedContainers.indexOf(defaultPrevContainer) + 1
          }
          else if (mergedContainers.includes(defaultNextContainer)) {
            insertIndex = mergedContainers.indexOf(defaultNextContainer)

          }
          else if (mergedContainers.includes(9) && mergedContainers.indexOf(9) === mergedContainers.length - 1) {
            insertIndex = mergedContainers.indexOf(9)
          }

          mergedContainers.splice(insertIndex, 0, container);
          activeContainers.value.push(container);
        });
      }

      if (updateStorage) containerOrder.value = [...mergedContainers];
    }

    watchEffect(() => {
      if (!crContainerOrderLoaded.value) return;
      
      containerCheck();
    });

    watchEffect(() => console.log(spotStore.spotId))

    return {
      showCompleteReportSyncControl,
      containers,
      activeContainers,
      containerOrder,
      lastUpdateTime,
      components,
      requests,
      isLoading,
      error,
      currentConditionsStartingView,
      windGraphStartingView,
      proForecastStartingView,
      showSettingsModal,
      getContainers,
      userChangedSettings,
      addContainer,
      insertContainer,
      clearContainers,
      resetContainers,
      containerCheck,
      updateLoadingStatus,
    }
});
